import React, { useState, useEffect } from 'react';
import { showAppAlert } from '../utils/alertService';
import { findInvalidEmailRecipients, parseKnownProviderEmails } from '../utils/emailValidation';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;

function AddAlertModal({ isOpen, onClose, onAddAlert, initialRecipients }) {
  const [formData, setFormData] = useState({
    nome: '',
    mensagem: '',
    destinatarios: [],
    data_inicio: '',
    data_termino: '',
  });

  const [manualRecipient, setManualRecipient] = useState('');
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [recipientSelection, setRecipientSelection] = useState('');

  // Limpa o formulário quando o modal é aberto
  useEffect(() => {
    if (isOpen) {
      const recipients = initialRecipients || [];
      // Verifica se os destinatários iniciais são e-mails manuais ou tags
      const hasManualEmails = recipients.some(r => r.includes('@'));
      
      setFormData({
        nome: '',
        mensagem: '',
        destinatarios: recipients.length > 0 ? recipients : [],
        data_inicio: '',
        data_termino: '',
      });
      setManualRecipient('');
      setIsManualEntry(hasManualEmails);
      
      if (recipients.includes('all')) {
        setRecipientSelection('all_clients');
      } else {
        setRecipientSelection(hasManualEmails ? 'manual' : '');
      }
    }
  }, [isOpen, initialRecipients]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (e) => {
    const selectedValue = e.target.value;

    if (!selectedValue) {
      setRecipientSelection('');
      return;
    }

    if (selectedValue === 'manual') {
      setIsManualEntry(true);
      if (formData.destinatarios.includes('all')) {
        setFormData(prev => ({ ...prev, destinatarios: [] }));
      }
      setRecipientSelection('manual');
    } else if (selectedValue === 'all_clients') {
      setIsManualEntry(false);
      setFormData(prev => ({ ...prev, destinatarios: ['all'] }));
      setRecipientSelection('all_clients');
    } else {
      setIsManualEntry(false);
      if (formData.destinatarios.includes(selectedValue)) {
        showAppAlert('info', 'Este segmento já foi adicionado.');
        setRecipientSelection(''); // Reset select
        return;
      }

      const newDestinatarios = formData.destinatarios.filter(d => d !== 'all');
      setFormData(prev => ({
        ...prev,
        destinatarios: [...newDestinatarios, selectedValue],
      }));
      setRecipientSelection(''); // Reset select to allow adding more
    }
  };

  const handleManualRecipientBlur = () => {
    if (!manualRecipient.trim()) return;

    const { valid, invalid } = parseKnownProviderEmails(
      manualRecipient,
      formData.destinatarios
    );

    if (invalid.length > 0) {
      showAppAlert('warning', `Os seguintes e-mails são inválidos ou não pertencem a provedores permitidos: ${invalid.join(', ')}`);
    }

    if (valid.length > 0) {
      setFormData(prev => ({
        ...prev,
        destinatarios: [...prev.destinatarios, ...valid],
      }));
    }

    setManualRecipient('');
  };

  const removeRecipient = (recipientToRemove) => {
    const newDestinatarios = formData.destinatarios.filter(r => r !== recipientToRemove);
    setFormData({
      ...formData,
      destinatarios: newDestinatarios,
    });
    if (recipientToRemove === 'all') {
      setRecipientSelection('');
    }
  };

  const validateDataInicio = () => {
    const today = new Date().toLocaleDateString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .split('/')
      .reverse()
      .join('-');
    if (formData.data_inicio < today) {
      showAppAlert('warning', 'A data de início não pode ser inferior à data de hoje.');
      setFormData({ ...formData, data_inicio: '' });
    }
  };

  const validateDataTermino = () => {
    if (formData.data_termino < formData.data_inicio) {
      formData.data_termino = formData.data_inicio;
      showAppAlert('warning', 'A data de término não pode ser menor que a data de início.');
      setFormData({ ...formData, data_termino: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const today = new Date().toLocaleDateString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .split('/')
      .reverse()
      .join('-');
    if (formData.data_inicio < today) {
      showAppAlert('warning', 'A data de início não pode ser inferior à data de hoje.');
      return;
    }
    if (formData.data_termino < formData.data_inicio) {
      showAppAlert('warning', 'A data de término não pode ser menor que a data de início.');
      return;
    }

    if (formData.destinatarios.length === 0) {
      showAppAlert('warning', 'O destinatário não pode estar vazio.');
      return;
    }

    const invalidRecipients = findInvalidEmailRecipients(formData.destinatarios);
    if (invalidRecipients.length > 0) {
      showAppAlert('warning', `Remova os e-mails inválidos ou de provedores não permitidos: ${invalidRecipients.join(', ')}`);
      return;
    }

    const companyId = localStorage.getItem('company_id');
    const token = localStorage.getItem('token');

    const alertData = {
      alert_name: formData.nome,
      alert_msg: formData.mensagem,
      recipients: JSON.stringify(formData.destinatarios),
      status: true,
      start_date: `${formData.data_inicio} 08:00:00`,
      end_date: `${formData.data_termino} 23:59:59`,
      company_id: companyId,
    };

    try {
      const response = await fetch(`${API_URL}/alerts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(alertData),
      });

      if (response.ok) {
        showAppAlert('success', 'Alerta cadastrado com sucesso.');
        const newAlert = await response.json();
        onAddAlert(newAlert);
        onClose();
      } else {
        // tenta extrair mensagem de erro retornada pela API e exibir ao usuário
        let errorMsg = 'Erro ao salvar o alerta. Verifique os dados e tente novamente.';
        try {
          const data = await response.json();
          if (data) {
            errorMsg = data.error || data.message || JSON.stringify(data);
          }
        } catch (e) {
          // resposta vazia ou não-JSON -> manter mensagem genérica
        }
        showAppAlert('error', errorMsg);
      }
    } catch (error) {
      console.error('Erro ao salvar o alerta:', error);
      showAppAlert('error', 'Erro ao salvar o alerta. Tente novamente mais tarde.');
    }
  };

  const formatRecipient = (recipient) => {
    if (recipient === 'all') return 'Todos os Clientes Ativos';
    if (recipient.startsWith('gender:')) {
      const gender = recipient.split(':')[1];
      return `Gênero: ${gender.charAt(0).toUpperCase() + gender.slice(1)}`;
    }
    if (recipient.startsWith('age:')) {
      const range = recipient.split(':')[1];
      if (range === '0-18') return 'Idade: Até 18 anos';
      if (range === '60-999') return 'Idade: Acima de 60 anos';
      return `Idade: ${range.replace('-', ' a ')} anos`;
    }
    if (recipient.startsWith('location:')) {
      const loc = recipient.split(':')[1];
      return `Localização: ${loc}`;
    }
    return recipient;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Adicionar Alerta</h3>
        <form onSubmit={handleSubmit}>
          {/* Campo Nome */}
          <label className="label-style">
            Nome:
            <input
              type="text"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              className="input-style"
              required
            />
          </label>

          {/* Campo Mensagem */}
          <label className="label-style">
            Mensagem:
            <textarea
              name="mensagem"
              value={formData.mensagem}
              onChange={handleChange}
              className="input-style"
              rows="3"
              required
            />
          </label>

          {/* Campo Destinatários */}
          <label className="label-style">
            Destinatários:
            <select
              name="destinatarios"
              onChange={handleSelectChange}
              className="input-style"
              value={recipientSelection}
            >
              <option value="">Selecione para adicionar...</option>
              <option value="all_clients">Todos os Clientes Ativos</option>
              <optgroup label="Gênero">
                <option value="gender:masculino">Masculino</option>
                <option value="gender:feminino">Feminino</option>
              </optgroup>
              <optgroup label="Faixa Etária">
                <option value="age:0-18">Até 18 anos</option>
                <option value="age:19-35">19 a 35 anos</option>
                <option value="age:36-59">36 a 59 anos</option>
                <option value="age:60-999">Acima de 60 anos</option>
              </optgroup>
              <option value="manual">Inserir Manualmente</option>
            </select>
          </label>

          {/* Campo de texto dinâmico para destinatários externos */}
          {isManualEntry && (
            <label className="label-style">
              Inserir Destinatários Manualmente (separados por vírgula):
              <input
                type="text"
                name="manualRecipient"
                value={manualRecipient}
                onChange={(e) => setManualRecipient(e.target.value)}
                onBlur={handleManualRecipientBlur}
                className="input-style"
                placeholder="usuario@gmail.com, usuario@icloud.com"
              />
            </label>
          )}

          {formData.destinatarios.length > 0 && (
            <div className="recipients-display">
              {formData.destinatarios.slice(0, 10).map((recipient, index) => (
                <span key={index} className="recipient-tag">
                  {formatRecipient(recipient)}
                  <button type="button" onClick={() => removeRecipient(recipient)}>
                    &times;
                  </button>
                </span>
              ))}
              {formData.destinatarios.length > 10 && (
                <span className="recipient-tag-more">
                  ... e mais {formData.destinatarios.length - 10} destinatário(s)
                </span>
              )}
            </div>
          )}

          {/* Campos de Data */}
          <div className="form-row">
            <div className="flex-1">
              <label className="label-style">
                Data Início:
                <DatePicker
                  selected={formData.data_inicio ? new Date(formData.data_inicio + 'T00:00:00') : null}
                  onChange={(date) => setFormData({ ...formData, data_inicio: date.toISOString().split('T')[0] })}
                  onBlur={validateDataInicio}
                  className="input-style"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="dd/mm/aaaa"
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={100}
                  required
                />
              </label>
            </div>
            <div className="flex-1">
              <label className="label-style">
                Data Término:
                <DatePicker
                  selected={formData.data_termino ? new Date(formData.data_termino + 'T00:00:00') : null}
                  onChange={(date) => setFormData({ ...formData, data_termino: date.toISOString().split('T')[0] })}
                  onBlur={validateDataTermino}
                  className="input-style"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="dd/mm/aaaa"
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={100}
                  required
                />
              </label>
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="button-container">
            <button type="submit" className="button-style left">
              Salvar
            </button>
            <button type="button" className="button-style cancel right" onClick={onClose}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddAlertModal;